import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "./ui";
import { STATUS_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Search, Download, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { first_name: "", last_name: "", phone: "", email: "", dob: "", notes: "" };

export default function Customers() {
    const { t, lang } = useI18n();
    const { user } = useAuth();
    const qc = useQueryClient();
    const [search, setSearch] = useState("");
    const [openD, setOpenD] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [profileId, setProfileId] = useState(null);

    const { data: customers, isLoading } = useQuery({
        queryKey: ["customers", search],
        queryFn: async () => (await api.get(`/customers${search ? `?search=${encodeURIComponent(search)}` : ""}`)).data,
    });
    const { data: profile } = useQuery({
        queryKey: ["customer", profileId],
        queryFn: async () => (await api.get(`/customers/${profileId}`)).data,
        enabled: !!profileId,
    });

    const openNew = () => { setEditing(null); setForm(empty); setOpenD(true); };
    const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpenD(true); };

    const save = async () => {
        setSaving(true);
        try {
            if (editing) await api.patch(`/customers/${editing.id}`, form);
            else await api.post("/customers", form);
            qc.invalidateQueries({ queryKey: ["customers"] });
            setOpenD(false); toast.success(t("common.saved"));
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };

    const remove = async (id) => {
        try { await api.delete(`/customers/${id}`); qc.invalidateQueries({ queryKey: ["customers"] }); setProfileId(null); toast.success(t("common.saved")); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const exportData = async () => {
        try {
            const { data } = await api.get("/customers/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "customers.json"; a.click();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <PageHeader title={t("customers.title")}
                action={
                    <div className="flex gap-2">
                        {user?.role === "owner" && <Button variant="outline" onClick={exportData} className="rounded-full" data-testid="export-customers"><Download className="mr-1 h-4 w-4" />{t("customers.export")}</Button>}
                        <Button onClick={openNew} className="rounded-full" data-testid="new-customer-btn"><Plus className="mr-1 h-4 w-4" />{t("customers.newCustomer")}</Button>
                    </div>
                } />

            <div className="relative mb-5 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder={t("common.search")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="customer-search" className="pl-9" />
            </div>

            {isLoading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>
                : customers.length === 0 ? <EmptyState icon={Users} title={t("empty.customers")} action={<Button onClick={openNew} className="rounded-full"><Plus className="mr-1 h-4 w-4" />{t("customers.newCustomer")}</Button>} />
                    : (
                        <div className="overflow-hidden rounded-2xl border border-border/70 bg-card">
                            <table className="w-full text-sm">
                                <thead className="border-b border-border/60 text-left text-xs uppercase text-muted-foreground">
                                    <tr><th className="px-4 py-3">{t("common.name")}</th><th className="px-4 py-3">{t("common.phone")}</th><th className="hidden px-4 py-3 sm:table-cell">{t("common.email")}</th></tr>
                                </thead>
                                <tbody>
                                    {customers.map((c) => (
                                        <tr key={c.id} data-testid={`customer-${c.id}`} onClick={() => setProfileId(c.id)} className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-secondary/40">
                                            <td className="px-4 py-3 font-medium">{c.first_name} {c.last_name}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{c.phone || "—"}</td>
                                            <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell">{c.email || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

            {/* Add/Edit dialog */}
            <Dialog open={openD} onOpenChange={setOpenD}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? t("common.edit") : t("customers.newCustomer")}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>{t("customers.firstName")}</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="customer-firstname" className="mt-1.5" /></div>
                            <div><Label>{t("customers.lastName")}</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>{t("common.phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>{t("common.email")}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email" className="mt-1.5" /></div>
                        </div>
                        <div><Label>{t("customers.internalNotes")}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenD(false)} className="rounded-full">{t("common.cancel")}</Button>
                        <Button onClick={save} disabled={saving || !form.first_name} className="rounded-full" data-testid="save-customer">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Profile dialog */}
            <Dialog open={!!profileId} onOpenChange={(o) => !o && setProfileId(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>{t("customers.profile")}</DialogTitle></DialogHeader>
                    {!profile ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /> : (
                        <div>
                            <h3 className="font-serif text-xl font-bold">{profile.first_name} {profile.last_name}</h3>
                            <p className="text-sm text-muted-foreground">{[profile.phone, profile.email].filter(Boolean).join(" · ")}</p>
                            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                                <div className="rounded-xl bg-secondary/50 p-3"><p className="font-serif text-lg font-bold">€{profile.total_spent.toFixed(0)}</p><p className="text-xs text-muted-foreground">{t("customers.totalSpent")}</p></div>
                                <div className="rounded-xl bg-secondary/50 p-3"><p className="font-serif text-lg font-bold">{profile.appointment_count}</p><p className="text-xs text-muted-foreground">{t("customers.appointments")}</p></div>
                                <div className="rounded-xl bg-secondary/50 p-3"><p className="font-serif text-lg font-bold">{profile.no_shows}</p><p className="text-xs text-muted-foreground">{t("dashboard.noShows")}</p></div>
                            </div>
                            {profile.notes && <p className="mt-4 rounded-xl border border-border/70 bg-card p-3 text-sm">{profile.notes}</p>}
                            <h4 className="mt-5 mb-2 text-sm font-semibold">{t("customers.history")}</h4>
                            <div className="max-h-48 space-y-2 overflow-y-auto">
                                {profile.appointments.length === 0 && <p className="text-sm text-muted-foreground">{t("empty.appointments")}</p>}
                                {profile.appointments.map((a) => (
                                    <div key={a.id} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 text-sm">
                                        <span>{new Date(a.start).toLocaleDateString(lang, { day: "2-digit", month: "short", year: "numeric" })}</span>
                                        <span className="rounded-full px-2 py-0.5 text-xs" style={{ background: STATUS_META[a.status]?.bg, color: STATUS_META[a.status]?.color }}>{t(`status.${a.status}`)}</span>
                                    </div>
                                ))}
                            </div>
                            <DialogFooter className="mt-5">
                                <Button variant="outline" onClick={() => openEdit(profile)} className="rounded-full">{t("common.edit")}</Button>
                                {user?.role === "owner" && <Button variant="ghost" onClick={() => remove(profile.id)} className="rounded-full text-destructive" data-testid="delete-customer"><Trash2 className="mr-1 h-4 w-4" />{t("common.delete")}</Button>}
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
